import React, { useRef, useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  Modal, Rate, Input, message, Button,
  Typography, Divider, Image
} from 'antd';
import {
  DownloadOutlined, CameraOutlined, SmileOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import { useParams, useLocation } from 'react-router-dom';
import './style.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const CameraCaptureWithMask = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const { name = "Guest", method = "Link" } = location.state || {};

  const webcamRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [matchedPhotos, setMatchedPhotos] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const captureAndMatch = async () => {
    if (!webcamRef.current) return message.warning("Camera not ready");

    try {
      const captured = webcamRef.current.getScreenshot();
      if (!captured) throw new Error("Capture failed");
      setImageSrc(captured);

      const blob = await fetch(captured).then(r => r.blob());
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('image', file);
      formData.append('event_id', eventId);

      const res = await axios.post('http://127.0.0.1:5000/match_faces', formData);
      if (res.data.matches?.length) {
        setMatchedPhotos(res.data.matches);
        setErrorMessage('');
        message.success("Matched photos found!");
      } else {
        setMatchedPhotos([]);
        setErrorMessage("No matching photos found");
        message.warning("No match found.");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
  };

  const downloadAll = async () => {
    const zip = new JSZip();
    const folder = zip.folder("Matched_Photos");

    await Promise.all(matchedPhotos.map(async (photo) => {
      const url = `http://localhost:5000/uploads/${photo.name}`;
      const res = await fetch(url);
      const blob = await res.blob();
      folder.file(photo.name, blob);
    }));

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, "Matched_Photos.zip");
  };

  const submitFeedback = async () => {
    if (!rating || !comment) return message.warning("Please fill all fields");
    message.success("Thank you for your feedback!");
    setFeedbackVisible(false);
    setRating(0);
    setComment('');
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "30px",
        backgroundImage: "url('/images/image1.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backdropFilter: "blur(3px)"
      }}
    >
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "rgba(255,255,255,0.9)",
          padding: "30px",
          borderRadius: "10px",
          boxShadow: "0 0 10px rgba(0,0,0,0.2)"
        }}
      >
        <Title level={2} style={{ color: "#d50039", textAlign: "center" }}>
          Welcome to PicAura!
        </Title>

        <Paragraph style={{ fontSize: "15px", color: "#555", textAlign: "center" }}>
          Take a clear selfie below to let PicAura match your face with photos from this event.
          Ensure proper lighting and a front-facing pose for best results.
        </Paragraph>

        <Divider />

        {!imageSrc && (
          <div style={{ textAlign: 'center' }}>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              width={400}
              style={{ borderRadius: "10px", border: "2px solid #ddd" }}
            />
            <br />
            <Button
              icon={<CameraOutlined />}
              type="primary"
              onClick={captureAndMatch}
              className="mt-3"
              style={{ backgroundColor: "#1677ff", marginTop: "20px" }}
            >
              Capture & Match
            </Button>
          </div>
        )}

        {imageSrc && (
          <>
            <Title level={4} className="mt-4" style={{ color: "#1677ff" }}>Your Captured Selfie</Title>
            <Image src={imageSrc} width={250} className="rounded shadow" />
            <br />
            <Button
              type="default"
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                setImageSrc(null);
                setMatchedPhotos([]);
                setErrorMessage('');
              }}
              style={{ marginTop: 20 }}
            >
              Retake Selfie
            </Button>
          </>
        )}

        {matchedPhotos.length > 0 && (
          <>
            <Title level={4} className="mt-4" style={{ color: "#1677ff" }}>Matched Photos</Title>
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {matchedPhotos.map((photo, i) => (
                <Image
                  key={i}
                  width={180}
                  src={`http://localhost:5000/uploads/${photo.name}`}
                  className="rounded shadow"
                />
              ))}
            </div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={downloadAll}
              className="mt-3"
              style={{ backgroundColor: "#1677ff" }}
            >
              Download All
            </Button>
          </>
        )}

        {errorMessage && (
          <div className="mt-4">
            <Text type="danger" style={{ fontSize: "15px" }}>{errorMessage}</Text>
          </div>
        )}

        {/* Feedback Floating Button */}
        <Button
          type="primary"
          shape="circle"
          icon={<SmileOutlined />}
          size="large"
          className="feedback-floating-btn"
          onClick={() => setFeedbackVisible(true)}
          style={{
            backgroundColor: "#52c41a",
            position: "fixed",
            right: "30px",
            bottom: "30px"
          }}
        />

        {/* Feedback Modal */}
        <Modal
          title="Feedback Form"
          open={feedbackVisible}
          onOk={submitFeedback}
          onCancel={() => setFeedbackVisible(false)}
        >
          <p>Name: {name}</p>
          <p>Rate your experience:</p>
          <Rate value={rating} onChange={setRating} />
          <TextArea
            rows={4}
            placeholder="Write your feedback"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Modal>
      </div>
    </div>
  );
};

export default CameraCaptureWithMask;
